import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  discordDocsPages as discordPages,
  getDocPageByPath,
  formatDiscordDate,
} from '@/data/discordDocsUrls'
import OutdatedBanner from './OutdatedBanner'
import styles from './DiscordDocument.module.css'

interface DiscordDocumentProps {
  language: 'cs' | 'en'
  onLanguageChange: (language: 'cs' | 'en') => void
  heading: ReactNode
  notice: ReactNode
  children: ReactNode
}

export default function DiscordDocument({
  language,
  onLanguageChange,
  heading,
  notice,
  children,
}: DiscordDocumentProps) {
  const { pathname, hash } = useLocation()
  const body = useRef<HTMLDivElement>(null)
  const documentRoot = useRef<HTMLDivElement>(null)
  const documentHeading = useRef<HTMLElement>(null)
  const [sections, setSections] = useState<{ id: string; title: string }[]>([])
  const config = getDocPageByPath(pathname)
  const cs = language === 'cs'

  useEffect(() => {
    if (hash) return
    documentHeading.current?.scrollIntoView({ block: 'start' })
    documentHeading.current?.focus({ preventScroll: true })
  }, [pathname, hash])

  useEffect(() => {
    const headings = [...(body.current?.querySelectorAll<HTMLElement>('h2') ?? [])]
    const items = headings.map((heading, index) => {
      const candidate = heading.closest<HTMLElement>('[id]')
      const section = candidate && body.current?.contains(candidate) ? candidate : null
      if (!section) heading.id ||= `doc-section-${index + 1}`
      return { id: section?.id || heading.id, title: heading.textContent?.trim() || '' }
    })
    setSections(
      items.filter(
        (item, index) => item.title && items.findIndex((other) => other.id === item.id) === index,
      ),
    )
    if (!hash) return
    let id: string
    try {
      id = decodeURIComponent(hash.slice(1))
    } catch {
      return
    }
    const target = document.getElementById(id)
    if (target && documentRoot.current?.contains(target)) {
      if (target instanceof HTMLDetailsElement) target.open = true
      target.tabIndex = -1
      target.scrollIntoView({ block: 'start' })
      target.focus({ preventScroll: true })
    }
  }, [language, pathname, hash])

  const navigation = (
    <ul>
      {discordPages.map((page) => (
        <li key={page.path}>
          <Link to={page.path} aria-current={pathname === page.path ? 'page' : undefined}>
            {cs ? page.csTitle : page.title}
          </Link>
        </li>
      ))}
    </ul>
  )

  return (
    <div className={`${styles.document} ac-width`} lang={language} ref={documentRoot}>
      <aside className={styles.sidebar}>
        <nav aria-label={cs ? 'Dokumentace Discordu' : 'Discord documentation'}>
          <Link className={styles.backLink} to="/discord">
            {cs ? 'Všechny dokumenty' : 'All documents'}
          </Link>
          {navigation}
        </nav>
      </aside>
      <div className={styles.reading}>
        <div className={styles.toolbar}>
          <Link to="/discord">{cs ? 'Dokumentace Discordu' : 'Discord documentation'}</Link>
          <div
            className={styles.langToggle}
            role="group"
            aria-label={cs ? 'Jazyk dokumentu' : 'Document language'}
          >
            <button
              type="button"
              aria-pressed={cs}
              onClick={() => onLanguageChange('cs')}
              lang="cs"
            >
              Česky
            </button>
            <button
              type="button"
              aria-pressed={!cs}
              onClick={() => onLanguageChange('en')}
              lang="en"
            >
              English
            </button>
          </div>
        </div>
        <details className={styles.mobileNav}>
          <summary>{cs ? 'Další dokumenty' : 'Other documents'}</summary>
          <nav aria-label={cs ? 'Dokumenty' : 'Documents'}>{navigation}</nav>
        </details>
        <header className={styles.documentHeading} ref={documentHeading} tabIndex={-1}>
          {heading}
          {config && (
            <div className={styles.meta}>
              <span>
                {cs ? 'Přeložená verze originálu: ' : 'Translated source edition: '}
                <time dateTime={config.ourDate}>{formatDiscordDate(config.ourDate, language)}</time>
              </span>
              {config.effectiveDate && (
                <span>
                  {cs ? 'Účinnost této verze: ' : 'This edition effective: '}
                  <time dateTime={config.effectiveDate}>
                    {formatDiscordDate(config.effectiveDate, language)}
                  </time>
                </span>
              )}
              <span>
                {cs ? 'Překlad upraven: ' : 'Translation edited: '}
                <time dateTime={config.translationUpdatedAt}>
                  {formatDiscordDate(config.translationUpdatedAt, language)}
                </time>
              </span>
              <span>
                {cs ? 'Poslední porovnání: ' : 'Last compared: '}
                <time dateTime={config.checkedAt}>
                  {formatDiscordDate(config.checkedAt, language)}
                </time>
              </span>
            </div>
          )}
        </header>
        <div className={styles.source}>
          <p>
            {cs
              ? 'Komunitní překlad Allcomu. Nejsme spojeni s Discord Inc.'
              : 'A community translation by Allcom. We are not affiliated with Discord Inc.'}
          </p>
          {config && (
            <a href={config.originalUrl} target="_blank" rel="noopener noreferrer">
              {cs ? 'Číst originál na Discordu' : 'Read the original on Discord'}
            </a>
          )}
        </div>
        <OutdatedBanner language={language} />
        {config?.history.length ? (
          <details className={`${styles.notice} ${styles.history}`} id="translation-history">
            <summary>{cs ? 'Co se změnilo' : 'What changed'}</summary>
            {config.history.map((revision) => (
              <section key={revision.date}>
                <h2>
                  <time dateTime={revision.date}>{formatDiscordDate(revision.date, language)}</time>
                </h2>
                <p>
                  {cs ? 'Porovnání originálu: ' : 'Source comparison: '}
                  {formatDiscordDate(revision.previousSourceDate, language)}
                  {revision.previousSourceDate !== revision.sourceDate &&
                    ` → ${formatDiscordDate(revision.sourceDate, language)}`}
                </p>
                <ul>
                  {revision.changes.map((change, index) => (
                    <li key={index}>
                      <strong>
                        {change.kind === 'source'
                          ? cs
                            ? 'Změna originálu'
                            : 'Source change'
                          : change.kind === 'translation'
                            ? cs
                              ? 'Úprava překladu'
                              : 'Translation edit'
                            : cs
                              ? 'Kontrola'
                              : 'Review'}
                        :{' '}
                      </strong>
                      {change[language]}
                      {change.sectionId && (
                        <>
                          {' '}
                          <a href={`#${change.sectionId}`}>
                            {cs ? 'Přejít na část' : 'Go to section'}
                          </a>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </details>
        ) : null}
        <details className={styles.notice}>
          <summary>{cs ? 'O překladu a odkazech' : 'About this translation and its links'}</summary>
          {notice}
        </details>
        {sections.length > 0 && (
          <details className={styles.toc} key={`${pathname}-${language}`}>
            <summary>{cs ? 'Na této stránce' : 'On this page'}</summary>
            <nav aria-label={cs ? 'Obsah dokumentu' : 'Table of contents'}>
              <ol>
                {sections.map((section) => (
                  <li key={section.id}>
                    <a href={`#${section.id}`}>{section.title}</a>
                  </li>
                ))}
              </ol>
            </nav>
          </details>
        )}
        <div className={styles.body} ref={body}>
          {children}
        </div>
        <footer className={styles.documentFooter}>
          <Link to="/discord">{cs ? 'Zpět na všechny dokumenty' : 'Back to all documents'}</Link>
          <a href="https://discord.allcom.zone" target="_blank" rel="noopener noreferrer">
            {cs ? 'Nahlásit chybu v překladu' : 'Report a translation issue'}
          </a>
        </footer>
      </div>
    </div>
  )
}
